import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

// withCredentials=true ensures the httpOnly auth cookie is sent on every request
export const api = axios.create({ baseURL: API, withCredentials: true });

// Endpoints that probe auth state — a 401 here is EXPECTED before login and must not trigger redirect/toast
const SILENT_AUTH_PROBE_PATHS = ["/auth/me", "/notifications/counts"];

// Endpoints that should NEVER be retried via /auth/refresh interceptor:
//  - the refresh endpoint itself (avoid infinite loop)
//  - login / logout / msal-login (issue or revoke tokens themselves)
const NO_REFRESH_RETRY_PATHS = ["/auth/refresh", "/auth/login", "/auth/logout", "/auth/msal-login"];

// Single-flight refresh promise — prevents a thundering herd of /refresh calls
// when several API requests fire concurrently after access token expires.
let _refreshPromise = null;

const tryRefresh = () => {
  if (!_refreshPromise) {
    _refreshPromise = axios
      .post(`${API}/auth/refresh`, null, { withCredentials: true })
      .finally(() => {
        // Reset after a microtask so callers awaiting this promise resolve first
        setTimeout(() => { _refreshPromise = null; }, 0);
      });
  }
  return _refreshPromise;
};

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err.response?.status;
    const original = err.config || {};
    const url = original.url || "";
    const isSilentProbe = SILENT_AUTH_PROBE_PATHS.some((p) => url.includes(p));
    const noRefreshRetry = NO_REFRESH_RETRY_PATHS.some((p) => url.includes(p));

    // ── Iter32: transparent access-token refresh on 401 ────────────────────
    if (status === 401 && !original._retried && !noRefreshRetry) {
      original._retried = true;
      try {
        await tryRefresh();
        // Cookie was updated by /refresh → retry the original request
        return api(original);
      } catch (refreshErr) {
        // Refresh failed → fall through to original 401 handling below.
        // Common in prod: refresh cookie expired after 7 days. Log at debug level
        // (not error) so real errors elsewhere aren't drowned out.
        console.debug("[api] refresh failed, falling back to 401 handling:", refreshErr?.message);
      }
    }

    if (status === 401) {
      if (!isSilentProbe && !window.location.pathname.startsWith("/login")) {
        // Session truly expired / refresh failed. Redirect to login and STOP propagation.
        window.location.href = "/login";
        return new Promise(() => {});
      }
    }
    return Promise.reject(err);
  }
);

export const downloadAsBlob = async (path, filename = "document.pdf") => {
  const r = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

export const hr = (iso) => {
  if (!iso) return "";
  try { const d = new Date(iso); return d.toLocaleDateString("hr-HR"); } catch { return iso; }
};

// Date + time in Croatian format: 24.05.2026. 14:32
export const hrDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("hr-HR")} ${d.toLocaleTimeString("hr-HR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return iso; }
};

export const STATUS_LABEL = {
  pending: "U postupku suglasnosti kod voditelja",
  approved: "Suglasan od voditelja",
  rejected: "Odbijeno",
  decided: "U izradi rješenja kod glavnog tajnika",
  sent: "Rješenje izrađeno",
};

export const REQUEST_TYPE_LABEL = {
  vacation: "Godišnji odmor",
  wfh: "Rad od kuće",
};

// Iter28: organizational-unit TYPE label map (matches backend ORG_UNIT_LABELS in models.py).
export const ORG_UNIT_LABEL = {
  katedra: "Katedra",
  odjel: "Odjel",
  zavod: "Zavod",
  sluzba: "Služba",
  knjiznica: "Knjižnica",
  racunovodstvo: "Računovodstvo/Financije",
  tajnistvo: "Tajništvo",
};

export const ORG_UNIT_OPTIONS = Object.entries(ORG_UNIT_LABEL).map(([value, label]) => ({ value, label }));

export const DEFAULT_ROLE_LABELS = {
  employee: "Zaposlenik",
  employee_teaching: "Zaposlenik — nastavno osoblje",
  employee_nonteaching: "Zaposlenik — nenastavno osoblje",
  prodekan: "Prodekan",
  manager: "Voditelj",
  secretary: "Glavni tajnik fakulteta",
  dean: "Dekan",
  admin: "Administrator",
};

// Mutable label map. Overridden at startup from /api/settings/role-labels (admin-customizable).
export const ROLE_LABEL = { ...DEFAULT_ROLE_LABELS };

export const applyRoleLabels = (overrides) => {
  if (!overrides || typeof overrides !== "object") return;
  Object.keys(DEFAULT_ROLE_LABELS).forEach(k => {
    ROLE_LABEL[k] = (overrides[k] && overrides[k].trim()) || DEFAULT_ROLE_LABELS[k];
  });
};

// Maps request status → shadcn Badge variant
export const statusBadgeVariant = (status) => {
  if (status === "rejected") return "destructive";
  if (status === "sent") return "default";
  return "secondary";
};

// Formats employee display name with academic title in correct position.
// Auto-detects placement from title content:
//  - contains "dr." (e.g. "prof. dr. sc.", "izv. prof. dr. sc.") → BEFORE name
//  - otherwise (e.g. "mag. ing. tech. inf.", "prof.")           → AFTER name (", ...")
export const detectTitlePosition = (at) => {
  const t = (at || "").toLowerCase();
  return (" " + t).includes(" dr.") ? "prefix" : "suffix";
};

export const formatEmployeeName = (u) => {
  if (!u) return "";
  const name = (u.full_name || "").trim();
  const at = (u.academic_title || u.title || "").trim();
  if (!at) return name;
  return detectTitlePosition(at) === "suffix" ? `${name}, ${at}` : `${at} ${name}`;
};

export const ROLE_OPTIONS = [
  { value: "employee_teaching", label: "Zaposlenik — nastavno osoblje" },
  { value: "employee_nonteaching", label: "Zaposlenik — nenastavno osoblje" },
  { value: "prodekan", label: "Prodekan" },
  { value: "manager", label: "Voditelj" },
  { value: "secretary", label: "Glavni tajnik fakulteta" },
  { value: "dean", label: "Dekan" },
  { value: "admin", label: "Administrator" },
];

export const EMPLOYEE_LIKE_ROLES = ["employee", "employee_teaching", "employee_nonteaching", "prodekan"];
export const isEmployeeLike = (r) => EMPLOYEE_LIKE_ROLES.includes(r);
